import { useState, useEffect } from "react";
import { Bell, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationItem } from "@/components/notifications/NotificationItem";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Notifications() {
  const { notifications, unreadCount, markAsRead, deleteNotification } = useNotifications();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    document.title = 'Dashi | Notificações';
  }, []);

  const unreadNotifications = notifications.filter(n => !n.is_read);
  const readNotifications = notifications.filter(n => n.is_read);

  const filterNotifications = (notifs: typeof notifications) => {
    if (!searchTerm) return notifs;
    
    const term = searchTerm.toLowerCase();
    return notifs.filter(n => 
      n.title.toLowerCase().includes(term) || 
      n.message.toLowerCase().includes(term)
    );
  };

  const filteredAll = filterNotifications(notifications);
  const filteredUnread = filterNotifications(unreadNotifications);
  const filteredRead = filterNotifications(readNotifications);

  const handleNotificationClick = (notificationId: string, isRead: boolean) => {
    if (!isRead) {
      markAsRead(notificationId);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bell className="h-8 w-8" />
            {t('notifications')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('manageYourNotifications')}
          </p>
        </div>

      {/* Search */}
      <Input
        placeholder={t('searchNotifications')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="max-w-md"
      />

      {/* Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">
            {t('allNotifications')} ({filteredAll.length})
          </TabsTrigger>
          <TabsTrigger value="unread">
            {t('unreadNotifications')} ({filteredUnread.length})
          </TabsTrigger>
          <TabsTrigger value="read">
            {t('readNotifications')} ({filteredRead.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {filteredAll.length > 0 ? (
            <div className="space-y-3">
              {filteredAll.map(notification => (
                <div key={notification.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <NotificationItem
                      notification={notification}
                      onClick={() => handleNotificationClick(notification.id, notification.is_read)}
                    />
                  </div>
                  <div className="flex gap-1 pt-3">
                    {!notification.is_read && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => markAsRead(notification.id)}
                        title={t('markAsRead')}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('delete')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir notificação?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteNotification(notification.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {t('delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState searchTerm={searchTerm} />
          )}
        </TabsContent>

        <TabsContent value="unread" className="mt-6">
          {filteredUnread.length > 0 ? (
            <div className="space-y-3">
              {filteredUnread.map(notification => (
                <div key={notification.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <NotificationItem
                      notification={notification}
                      onClick={() => handleNotificationClick(notification.id, notification.is_read)}
                    />
                  </div>
                  <div className="flex gap-1 pt-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => markAsRead(notification.id)}
                      title={t('markAsRead')}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('delete')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir notificação?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteNotification(notification.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {t('delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState searchTerm={searchTerm} message={t('noUnreadNotifications')} />
          )}
        </TabsContent>

        <TabsContent value="read" className="mt-6">
          {filteredRead.length > 0 ? (
            <div className="space-y-3">
              {filteredRead.map(notification => (
                <div key={notification.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <NotificationItem
                      notification={notification}
                      onClick={() => {}}
                    />
                  </div>
                  <div className="flex gap-1 pt-3">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('delete')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir notificação?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteNotification(notification.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {t('delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState searchTerm={searchTerm} message={t('noReadNotifications')} />
          )}
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}

function EmptyState({ searchTerm, message }: { searchTerm?: string; message?: string }) {
  const { t } = useTranslation();
  
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <Bell className="h-16 w-16 text-muted-foreground/50 mb-4" />
      <p className="text-muted-foreground">
        {searchTerm 
          ? t('noNotificationsFound')
          : message || t('noNotifications')
        }
      </p>
    </div>
  );
}
